import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, FlaskConical, Sparkles, Trash2, Info } from "lucide-react";

export default function DatosPruebaConfig() {
  const [estado, setEstado] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [borrando, setBorrando] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/demo/estado");
      setEstado(data);
    } catch (e) {
      /* noop */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generar = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post("/demo/generar");
      if (data.ya_existian > 0) {
        toast.info(data.mensaje || "Ya hay datos de prueba cargados.");
      } else {
        toast.success(`${data.creadas} reservas de prueba generadas. Mirá la Agenda de hoy.`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerando(false);
    }
  };

  const borrar = async () => {
    setBorrando(true);
    try {
      const { data } = await api.post("/demo/borrar");
      toast.success(`Datos de prueba borrados: ${data.reservas_borradas} reservas, ${data.salidas_borradas} salidas.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBorrando(false);
    }
  };

  const hayDatos = (estado?.reservas || 0) > 0;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="datos-prueba-config">
      <Card className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white shrink-0">
            <FlaskConical className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Datos de prueba</h3>
            <p className="text-sm text-slate-500 mt-1">
              Cargá un set realista de reservas repartidas en varios días y tours (regular, privado y VIP, con
              adultos, niños e infantes, algunas con saldo pendiente, una salida casi llena y una llena) para
              demostrar el flujo completo. Borralos antes de pasar a producción: solo afecta a los datos de
              prueba, nunca a reservas reales ni al catálogo.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 rounded-md bg-slate-50 border border-slate-200 px-4 py-3">
          <Info className="h-4 w-4 text-slate-400 shrink-0" />
          <p className="text-sm text-slate-600" data-testid="datos-prueba-estado">
            {estado === null ? (
              "Cargando estado…"
            ) : hayDatos ? (
              <>
                Actualmente hay <span className="font-semibold text-slate-900">{estado.reservas}</span> reserva(s) de
                prueba en <span className="font-semibold text-slate-900">{estado.salidas}</span> salida(s).
              </>
            ) : (
              "No hay datos de prueba cargados en este momento."
            )}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={generar} disabled={generando || hayDatos} data-testid="generar-datos-prueba-btn" className="gap-2">
            {generando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Generar datos de prueba
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={borrando || !hayDatos} data-testid="borrar-datos-prueba-btn" className="gap-2 text-red-600 hover:text-red-700">
                {borrando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Borrar datos de prueba
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="borrar-datos-prueba-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Borrar los datos de prueba?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se eliminarán únicamente las reservas y salidas marcadas como prueba, junto con sus respuestas,
                  solicitudes y registros de auditoría. Las reservas reales y el catálogo de tours no se tocan.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={borrar} className="bg-red-600 hover:bg-red-700" data-testid="confirmar-borrar-datos-prueba">
                  Sí, borrar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>
    </div>
  );
}
